/**
 * Vanguard Soft — Consultor de Arquitectura de Software & Diagnóstico de Marca
 * Flujo guiado de 6 fases para dimensionamiento técnico, auditoría digital,
 * requerimientos de Brandbook, dirección visual de interfaz y consolidación
 * de Ficha Integral de Proyecto para el equipo de diseño e ingeniería.
 * Estilo de ingeniería: sin emoticonos, sin botones pills, sin IA slop.
 */

class VanguardAIConsultant {
  constructor() {
    this.container = document.getElementById('ai-consultant-stage');
    this.thread = document.getElementById('ai-chat-thread');
    this.chipsContainer = document.getElementById('ai-chips-group');
    this.form = document.getElementById('ai-input-form');
    this.textInput = document.getElementById('ai-user-text');
    this.stepIndicator = document.getElementById('ai-step-indicator');
    this.progressBar = document.getElementById('duo-progress-bar');
    this.stepCountLabel = document.getElementById('duo-step-count');
    this.percentLabel = document.getElementById('duo-percent-label');

    this.state = {
      step: 1,
      scope: '',
      websiteOrSocial: '',
      brandStatus: '',
      designStyle: '',
      objective: '',
      preferredChannel: 'WhatsApp Directo',
      contact: {
        name: '',
        company: '',
        websiteOrSocial: '',
        emailOrPhone: '',
        notes: ''
      },
      dossierId: null
    };

    this.isTyping = false;
  }

  init() {
    if (!this.container || !this.thread) return;

    this.bindEvents();
    this.renderStep1();
  }

  bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const val = this.textInput.value.trim();
        if (!val || this.isTyping) return;
        this.handleUserTextInput(val);
        this.textInput.value = '';
      });
    }

    if (this.textInput) {
      this.textInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.form?.dispatchEvent(new Event('submit'));
        }
      });
    }
  }

  updateProgress(stepNumber, totalSteps, title) {
    const percent = Math.min(100, Math.round((stepNumber / totalSteps) * 100));
    if (this.progressBar) {
      this.progressBar.style.width = `${percent}%`;
    }
    if (this.stepCountLabel) {
      this.stepCountLabel.textContent = `Fase ${stepNumber} de ${totalSteps} · ${title}`;
    }
    if (this.percentLabel) {
      this.percentLabel.textContent = `${percent}%`;
    }
    if (this.stepIndicator) {
      this.stepIndicator.textContent = `Fase ${stepNumber} / ${totalSteps}`;
    }
  }

  appendBotMessage(htmlContent, callback) {
    this.isTyping = true;
    const msgEl = document.createElement('div');
    msgEl.className = 'ai-msg ai-msg-bot';
    msgEl.innerHTML = `
      <div class="ai-msg-avatar" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>
      </div>
      <div class="ai-msg-bubble">
        <div class="ai-typing-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    `;
    this.thread.appendChild(msgEl);
    this.scrollToBottom();

    setTimeout(() => {
      const bubble = msgEl.querySelector('.ai-msg-bubble');
      bubble.innerHTML = htmlContent;
      this.isTyping = false;
      this.scrollToBottom();
      if (callback) callback();
    }, 280);
  }

  appendUserMessage(text) {
    const msgEl = document.createElement('div');
    msgEl.className = 'ai-msg ai-msg-user';
    msgEl.innerHTML = `
      <div class="ai-msg-bubble">${this.escapeHTML(text)}</div>
    `;
    this.thread.appendChild(msgEl);
    this.scrollToBottom();
  }

  setChips(chips, onClick) {
    // Si hay chips previos activos en el thread o contenedor, limpiarlos
    this.clearChips();

    const chipsWrapper = document.createElement('div');
    chipsWrapper.className = 'ai-chips-inline-wrap';

    chips.forEach(chip => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ai-chip' + (chip.sub ? ' ai-chip-rich' : '') + (chip.isCustom ? ' ai-chip-custom' : '');
      
      const icon = chip.iconSvg 
        ? `<span class="chip-icon-svg" aria-hidden="true">${chip.iconSvg}</span>`
        : '';

      btn.innerHTML = `
        ${icon}
        <span class="chip-text-group">
          <span class="chip-label">${chip.label}</span>
          ${chip.sub ? `<span class="chip-sub">${chip.sub}</span>` : ''}
        </span>
      `;

      btn.addEventListener('click', () => {
        if (this.isTyping) return;
        onClick(chip);
      });
      chipsWrapper.appendChild(btn);
    });

    this.thread.appendChild(chipsWrapper);
    this.activeChipsWrapper = chipsWrapper;
    this.scrollToBottom();
  }

  clearChips() {
    if (this.activeChipsWrapper) {
      this.activeChipsWrapper.remove();
      this.activeChipsWrapper = null;
    }
    if (this.chipsContainer) {
      this.chipsContainer.innerHTML = '';
    }
  }

  scrollToBottom() {
    if (this.thread) {
      setTimeout(() => {
        this.thread.scrollTop = this.thread.scrollHeight;
      }, 50);
    }
  }

  focusInputWithPlaceholder(placeholderText) {
    if (this.textInput) {
      this.textInput.placeholder = placeholderText;
      this.textInput.focus();
    }
  }

  // ================= FASE 1: PUNTO DE PARTIDA & ALCANCE =================
  renderStep1() {
    this.state.step = 1;
    this.updateProgress(1, 6, 'Punto de Partida');

    const promptText = `
      Bienvenido a <strong>Vanguard</strong>.<br><br>
      Realizamos el diagnóstico técnico de tu negocio para preparar la arquitectura de software, auditoría de marca y el <strong>prototipo funcional en Semana 2</strong>.<br><br>
      Para estructurar el análisis: <strong>¿cuál es el punto de partida de este proyecto?</strong>
    `;

    this.appendBotMessage(promptText, () => {
      const chips = [
        {
          id: 'redesign',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>`,
          label: 'Rediseño y modernización de plataforma existente',
          sub: 'Auditoría de UI/UX, optimización de conversión y nueva arquitectura'
        },
        {
          id: 'from_scratch',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>`,
          label: 'Nuevo producto digital o plataforma desde cero',
          sub: 'Nueva marca o idea digital con prototipo funcional en Semana 2'
        },
        {
          id: 'internal_ops',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg>`,
          label: 'Evolución de software operativo interno',
          sub: 'Migración de hojas de cálculo o sistemas legacy a base de datos propia'
        },
        {
          id: 'custom_scope',
          isCustom: true,
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`,
          label: 'Requerimiento específico a medida...',
          sub: 'Describir el alcance en el campo inferior'
        }
      ];

      this.setChips(chips, (selected) => {
        if (selected.id === 'custom_scope') {
          this.focusInputWithPlaceholder('Describe brevemente el alcance de tu proyecto...');
        } else {
          this.state.scope = selected.label;
          this.appendUserMessage(selected.label);
          this.clearChips();
          this.renderStep2('Punto de partida registrado.');
        }
      });
    });
  }

  // ================= FASE 2: PRESENCIA DIGITAL ACTUAL & ENLACES =================
  renderStep2(validationText = '') {
    this.state.step = 2;
    this.updateProgress(2, 6, 'Presencia Digital & Redes');

    const msg = `
      ${validationText ? `<strong>${validationText}</strong><br><br>` : ''}
      Para que el equipo audite la presencia actual de tu negocio y analice la experiencia de usuario: <strong>¿cuentas con un sitio web activo o redes sociales?</strong>
    `;

    this.appendBotMessage(msg, () => {
      const chips = [
        {
          id: 'has_both',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`,
          label: 'Tengo sitio web activo y redes para auditar',
          sub: 'Ingresar URLs para análisis de interfaz y conversión'
        },
        {
          id: 'only_social',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>`,
          label: 'Solo contamos con redes sociales (Instagram / LinkedIn)',
          sub: 'Indicar perfiles para análisis de marca'
        },
        {
          id: 'no_presence',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>`,
          label: 'Aún no tenemos presencia digital pública',
          sub: 'Proyecto nuevo que parte desde una hoja en blanco'
        },
        {
          id: 'write_links',
          isCustom: true,
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>`,
          label: 'Escribir enlaces o perfiles en el campo inferior...',
          sub: 'Indicar URL o redes sociales'
        }
      ];

      this.setChips(chips, (selected) => {
        if (selected.id === 'has_both' || selected.id === 'only_social' || selected.id === 'write_links') {
          this.appendUserMessage(selected.label);
          this.clearChips();
          this.appendBotMessage(
            'Por favor escribe la URL de tu sitio web actual y/o tus perfiles de redes (ej. <em>miempresa.com · @miempresa en Instagram/LinkedIn</em>):',
            () => {
              this.focusInputWithPlaceholder('Ej. https://miempresa.com o @miempresa en redes...');
            }
          );
        } else {
          this.state.websiteOrSocial = 'Sin presencia digital previa (Proyecto desde cero)';
          this.appendUserMessage(selected.label);
          this.clearChips();
          this.renderStep3('Entendido. Partiremos desde cero en arquitectura y diseño.');
        }
      });
    });
  }

  // ================= FASE 3: IDENTIDAD DE MARCA & BRANDBOOK =================
  renderStep3(validationText = '') {
    this.state.step = 3;
    this.updateProgress(3, 6, 'Identidad & Brandbook');

    const msg = `
      ${validationText ? `<strong>${validationText}</strong><br><br>` : ''}
      Para iniciar el diseño correspondiente y alinear la línea gráfica: <strong>¿en qué estado se encuentra la identidad y Brandbook de tu marca?</strong>
    `;

    this.appendBotMessage(msg, () => {
      const chips = [
        {
          id: 'has_brandbook',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>`,
          label: 'Tengo Brandbook y manual de marca corporativo',
          sub: 'Logo vectorial, paleta cromática y tipografías definidas a respetar'
        },
        {
          id: 'needs_brandbook',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>`,
          label: 'Tengo logo, pero necesitamos crear el Brandbook y UI kit',
          sub: 'Diseño de guía de estilos, componentes UI y paleta de colores'
        },
        {
          id: 'branding_from_scratch',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"></path><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path><path d="M2 2l7.586 7.586"></path><circle cx="11" cy="11" r="2"></circle></svg>`,
          label: 'Requerimos diseño de identidad y branding desde cero',
          sub: 'Creación de concepto visual, logotipo, tipografía y manual'
        },
        {
          id: 'rebranding',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"></path><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>`,
          label: 'Queremos un Rebranding moderno de nuestra marca actual',
          sub: 'Evolución de imagen hacia una identidad visual de alta gama'
        }
      ];

      this.setChips(chips, (selected) => {
        this.state.brandStatus = selected.label;
        this.appendUserMessage(selected.label);
        this.clearChips();
        this.renderStep4('Requerimiento de identidad anotado.');
      });
    });
  }

  // ================= FASE 4: DIRECCIÓN ESTÉTICA & LENGUAJE VISUAL =================
  renderStep4(validationText = '') {
    this.state.step = 4;
    this.updateProgress(4, 6, 'Dirección Visual');

    const msg = `
      ${validationText ? `<strong>${validationText}</strong><br><br>` : ''}
      <strong>¿Qué estilo visual y nivel de sofisticación debe proyectar la interfaz de tu sistema?</strong>
    `;

    this.appendBotMessage(msg, () => {
      const chips = [
        {
          id: 'precision_tech',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>`,
          label: 'Ingeniería de precisión y alta tecnología',
          sub: 'Estilo Linear / Stripe: geometría limpia, modo claro y tipografía sobria'
        },
        {
          id: 'corporate_b2b',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>`,
          label: 'Corporativo institucional y solvencia B2B',
          sub: 'Formal, sobrio, estructuras sólidas y confianza enterprise'
        },
        {
          id: 'vanguard_dynamic',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>`,
          label: 'Moderno, vanguardista y dinámico',
          sub: 'Micro-interacciones fluidas, profundidad y dinamismo de conversión'
        },
        {
          id: 'minimalist_luxury',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="1"></rect><line x1="8" y1="9" x2="16" y2="9"></line><line x1="8" y1="13" x2="14" y2="13"></line></svg>`,
          label: 'Minimalismo editorial y refinamiento',
          sub: 'Espacios generosos, jerarquía tipográfica y foco puro en el producto'
        }
      ];

      this.setChips(chips, (selected) => {
        this.state.designStyle = selected.label;
        this.appendUserMessage(selected.label);
        this.clearChips();
        this.renderStep5('Dirección visual registrada.');
      });
    });
  }

  // ================= FASE 5: ARQUITECTURA TÉCNICA & OBJETIVO OPERATIVO =================
  renderStep5(validationText = '') {
    this.state.step = 5;
    this.updateProgress(5, 6, 'Arquitectura & Objetivo');

    const msg = `
      ${validationText ? `<strong>${validationText}</strong><br><br>` : ''}
      Para que los arquitectos de software dimensionen la solución técnica: <strong>¿cuál es la funcionalidad o desafío operativo prioritario que debe resolver la plataforma?</strong>
    `;

    this.appendBotMessage(msg, () => {
      const chips = [
        {
          id: 'sales_portal',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>`,
          label: 'Captación continua, cotizador o portal comercial',
          sub: 'Atención 24/7 y automatización del embudo de conversión'
        },
        {
          id: 'internal_db',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>`,
          label: 'Centralización operativa y eliminación de hojas de cálculo',
          sub: 'Base de datos PostgreSQL, roles RBAC y trazabilidad interna'
        },
        {
          id: 'integrations_wa',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>`,
          label: 'Integración con WhatsApp API, pasarelas de pago o ERP',
          sub: 'Cobros automáticos, webhooks y sincronización en tiempo real'
        },
        {
          id: 'mvp_week2',
          iconSvg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>`,
          label: 'Lanzamiento de MVP funcional escalable en Semana 2',
          sub: 'Prototipo navegable para validar con clientes e inversores'
        }
      ];

      this.setChips(chips, (selected) => {
        this.state.objective = selected.label;
        this.appendUserMessage(selected.label);
        this.clearChips();
        this.renderStep6();
      });
    });
  }

  // ================= FASE 6: FICHA INTEGRAL DE PROYECTO & ASIGNACIÓN =================
  renderStep6() {
    this.state.step = 6;
    this.updateProgress(6, 6, 'Ficha Integral');

    const summaryLead = `
      Diagnóstico integral completado. Hemos consolidado el expediente técnico y de diseño para que nuestro equipo de ingeniería inicie el análisis y la preparación del Brandbook:
    `;

    this.appendBotMessage(summaryLead, () => {
      const summaryCardHtml = `
        <div class="duo-summary-card" id="duo-summary-card">
          <div class="duo-card-top">
            <span class="duo-card-badge">Ficha Integral de Proyecto & Brand Analysis</span>
            <span class="duo-card-status">Lista para Asignación</span>
          </div>

          <div class="duo-summary-grid">
            <div class="duo-summary-item">
              <span class="duo-summary-label">Punto de Partida</span>
              <span class="duo-summary-val">${this.escapeHTML(this.state.scope || 'Proyecto a medida')}</span>
            </div>
            <div class="duo-summary-item">
              <span class="duo-summary-label">Presencia Actual / Enlaces</span>
              <span class="duo-summary-val highlight-blue">${this.escapeHTML(this.state.websiteOrSocial || 'Por especificar')}</span>
            </div>
            <div class="duo-summary-item">
              <span class="duo-summary-label">Brandbook & Identidad</span>
              <span class="duo-summary-val">${this.escapeHTML(this.state.brandStatus || 'Definir en propuesta')}</span>
            </div>
            <div class="duo-summary-item">
              <span class="duo-summary-label">Dirección Visual</span>
              <span class="duo-summary-val">${this.escapeHTML(this.state.designStyle || 'Ingeniería de precisión')}</span>
            </div>
            <div class="duo-summary-item" style="grid-column: 1 / -1;">
              <span class="duo-summary-label">Desafío Operativo / Objetivo</span>
              <span class="duo-summary-val">${this.escapeHTML(this.state.objective || 'Automatización operativa')}</span>
            </div>
          </div>

          <div class="duo-guarantee-pill">
            <span><strong>Garantía Vanguard:</strong> Prototipo navegable en Semana 2 · Código 100% propietario sin rentas · Entrega de Brandbook y guía de estilo.</span>
          </div>
        </div>

        <div class="duo-contact-wrap">
          <div class="duo-channel-title">Canal preferido para entrega del reporte y propuesta técnica:</div>
          
          <div class="duo-channel-selector" id="duo-channel-selector">
            <button type="button" class="duo-channel-btn active" data-channel="WhatsApp Directo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.007c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.275.072.376-.044c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.043.073.043.419-.101.824z"/></svg>
              <span>WhatsApp Directo</span>
            </button>
            <button type="button" class="duo-channel-btn" data-channel="Correo Corporativo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              <span>Correo Corporativo</span>
            </button>
            <button type="button" class="duo-channel-btn" data-channel="Sesión de 15 min">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
              <span>Sesión de 15 min</span>
            </button>
          </div>

          <div class="ai-contact-inline-form" id="ai-contact-fields">
            <div class="ai-inline-field">
              <label for="ai-name">Nombre y Apellido *</label>
              <input type="text" id="ai-name" placeholder="Ej. Carlos Mendoza" required autocomplete="name">
            </div>
            <div class="ai-inline-field">
              <label for="ai-company">Empresa o Proyecto *</label>
              <input type="text" id="ai-company" placeholder="Ej. Logística Global" required autocomplete="organization">
            </div>
            <div class="ai-inline-field">
              <label for="ai-web-social">Sitio web actual o redes sociales (Opcional)</label>
              <input type="text" id="ai-web-social" placeholder="https://miempresa.com o @miempresa" value="${this.state.websiteOrSocial.includes('Sin presencia') ? '' : this.escapeHTML(this.state.websiteOrSocial)}">
            </div>
            <div class="ai-inline-field">
              <label for="ai-contact" id="ai-contact-label">WhatsApp o Teléfono Directo *</label>
              <input type="text" id="ai-contact" placeholder="+34 612... o +58 412..." required autocomplete="tel">
            </div>
            <button type="button" class="ai-generate-blueprint-btn" id="ai-btn-finish">
              <span>Transferir Ficha a Diseño e Ingeniería</span>
              <span class="btn-arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      `;

      const cardEl = document.createElement('div');
      cardEl.className = 'ai-msg ai-msg-bot';
      cardEl.innerHTML = `
        <div class="ai-msg-avatar" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <div class="ai-msg-bubble">${summaryCardHtml}</div>
      `;
      this.thread.appendChild(cardEl);
      this.scrollToBottom();

      const channelBtns = cardEl.querySelectorAll('.duo-channel-btn');
      channelBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          channelBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.state.preferredChannel = btn.dataset.channel;
          const label = document.getElementById('ai-contact-label');
          const input = document.getElementById('ai-contact');
          if (btn.dataset.channel === 'Correo Corporativo') {
            if (label) label.textContent = 'Correo Corporativo *';
            if (input) { input.placeholder = 'carlos@empresa.com'; input.type = 'email'; }
          } else {
            if (label) label.textContent = 'WhatsApp o Teléfono Directo *';
            if (input) { input.placeholder = '+34 612... o +58 412...'; input.type = 'tel'; }
          }
        });
      });

      const finishBtn = document.getElementById('ai-btn-finish');
      if (finishBtn) {
        finishBtn.addEventListener('click', () => {
          this.handleContactSubmit();
        });
      }
    });
  }

  handleContactSubmit() {
    const nameInput = document.getElementById('ai-name');
    const companyInput = document.getElementById('ai-company');
    const webSocialInput = document.getElementById('ai-web-social');
    const contactInput = document.getElementById('ai-contact');

    const name = nameInput?.value.trim();
    const company = companyInput?.value.trim();
    const webSocial = webSocialInput?.value.trim();
    const contact = contactInput?.value.trim();

    if (!name || !company || !contact) {
      alert('Por favor completa los campos obligatorios para transferir la ficha técnica.');
      return;
    }

    if (webSocial) {
      this.state.websiteOrSocial = webSocial;
    }

    this.state.contact = { 
      name, 
      company, 
      websiteOrSocial: this.state.websiteOrSocial, 
      emailOrPhone: contact 
    };

    this.appendUserMessage(`${name} · ${company} · ${contact} (${this.state.preferredChannel})`);

    const syncBox = document.createElement('div');
    syncBox.className = 'ai-msg ai-msg-bot';
    syncBox.innerHTML = `
      <div class="ai-msg-avatar" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>
      </div>
      <div class="ai-msg-bubble">
        <div class="duo-syncing-state">
          <span class="duo-sync-spinner" aria-hidden="true"></span>
          <span>Consolidando auditoría y transfiriendo al equipo de diseño e ingeniería...</span>
        </div>
      </div>
    `;
    this.thread.appendChild(syncBox);
    this.scrollToBottom();

    setTimeout(() => {
      syncBox.remove();
      this.renderSuccessBlueprint();
    }, 600);
  }

  renderSuccessBlueprint() {
    this.state.dossierId = 'VG-' + Math.floor(1000 + Math.random() * 9000);

    const dossier = {
      id: this.state.dossierId,
      timestamp: new Date().toISOString(),
      client: this.state.contact,
      scope: this.state.scope,
      websiteOrSocial: this.state.websiteOrSocial,
      brandStatus: this.state.brandStatus,
      designStyle: this.state.designStyle,
      objective: this.state.objective,
      preferredChannel: this.state.preferredChannel
    };

    this.saveDossierToBackoffice(dossier);

    const successHtml = `
      <div class="ai-blueprint-wrapper" id="blueprint-result">
        <div class="bp-header">
          <div class="bp-badge"><span class="status-indicator-dot"></span> Expediente Asignado #${this.state.dossierId}</div>
          <div class="bp-date">Auditoría Técnica en Curso</div>
        </div>

        <h3 class="bp-title">Ficha integral transferida con éxito, ${this.escapeHTML(this.state.contact.name)}</h3>
        <p class="bp-lead">Un arquitecto de software y un diseñador UI senior de Vanguard han recibido el expediente técnico para ${this.escapeHTML(this.state.contact.company)}.</p>

        <div class="bp-milestone-box">
          <div class="bp-milestone-title">Garantía Vanguard:</div>
          <div class="bp-milestone-desc">
            <strong>Semana 2:</strong> Prototipo navegable y funcional en servidor de pruebas real, acompañado de la propuesta de Brandbook y guía de estilo. Código 100% propietario en repositorio Git privado sin costes recurrentes por usuario.
          </div>
        </div>

        <div class="bp-actions-row">
          <a href="${this.generateWhatsAppUrl()}" target="_blank" rel="noopener noreferrer" class="bp-btn bp-btn-wa">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.007c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.275.072.376-.044c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.043.073.043.419-.101.824z"/></svg>
            <span>Continuar con Equipo en WhatsApp</span>
          </a>
          <button type="button" class="bp-btn bp-btn-copy" id="bp-btn-copy">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            <span id="copy-btn-label">Copiar Ficha Integral</span>
          </button>
        </div>

        <div class="bp-footer-note">
          Expediente registrado en el Backoffice de Vanguard. Un consultor asignado revisará la presencia web y los lineamientos de diseño en menos de 1 hora hábil.
        </div>
      </div>
    `;

    this.appendBotMessage(successHtml, () => {
      const copyBtn = document.getElementById('bp-btn-copy');
      if (copyBtn) {
        copyBtn.addEventListener('click', () => {
          this.copyBlueprintText();
        });
      }
    });
  }

  generateWhatsAppUrl() {
    const text = 
      `Hola equipo Vanguard, he completado mi diagnóstico integral en el portal.\n\n` +
      `Expediente: ${this.state.dossierId}\n` +
      `Empresa: ${this.state.contact.company}\n` +
      `Contacto: ${this.state.contact.name} (${this.state.contact.emailOrPhone})\n` +
      `Punto de Partida: ${this.state.scope}\n` +
      `Presencia Actual / Enlaces: ${this.state.websiteOrSocial || 'No indicada'}\n` +
      `Brandbook & Identidad: ${this.state.brandStatus}\n` +
      `Dirección Visual: ${this.state.designStyle}\n` +
      `Desafío Operativo: ${this.state.objective}\n` +
      `Canal Preferido: ${this.state.preferredChannel}\n\n` +
      `Solicito auditoría previa, propuesta de Brandbook y validación de prototipo funcional en Semana 2.`;

    return `https://wa.me/584129585353?text=${encodeURIComponent(text)}`;
  }

  copyBlueprintText() {
    const text = 
      `FICHA INTEGRAL DE PROYECTO VANGUARD [${this.state.dossierId}]\n` +
      `--------------------------------------------------\n` +
      `Empresa: ${this.state.contact.company}\n` +
      `Contacto: ${this.state.contact.name} (${this.state.contact.emailOrPhone})\n` +
      `Punto de Partida: ${this.state.scope}\n` +
      `Presencia Actual / Enlaces: ${this.state.websiteOrSocial || 'No indicada'}\n` +
      `Brandbook & Identidad: ${this.state.brandStatus}\n` +
      `Dirección Visual: ${this.state.designStyle}\n` +
      `Desafío Operativo: ${this.state.objective}\n` +
      `Garantía: Prototipo funcional en Semana 2 · Brandbook · Cero rentas de software.\n` +
      `--------------------------------------------------\n` +
      `Generado en vanguardsoft.app`;

    navigator.clipboard.writeText(text).then(() => {
      const label = document.getElementById('copy-btn-label');
      if (label) {
        label.textContent = 'Copiado al Portapapeles';
        setTimeout(() => { label.textContent = 'Copiar Ficha Integral'; }, 2500);
      }
    }).catch(() => {
      alert('Ficha copiada al portapapeles.');
    });
  }

  saveDossierToBackoffice(dossier) {
    try {
      const existing = JSON.parse(localStorage.getItem('vanguard_client_dossiers') || '[]');
      existing.unshift({
        id: dossier.id,
        date: new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        clientName: dossier.client.name,
        company: dossier.client.company,
        contact: dossier.client.emailOrPhone,
        niche: dossier.scope,
        websiteOrSocial: dossier.websiteOrSocial,
        brandStatus: dossier.brandStatus,
        designStyle: dossier.designStyle,
        tag: 'Technical Lead',
        bottleneck: dossier.objective,
        volume: dossier.websiteOrSocial ? 'Auditoría Web + Brandbook + UI' : 'Brandbook + Prototipo Semana 2',
        stack: 'TypeScript · PostgreSQL · Figma / Brandbook UI',
        roi: 'Prototipo funcional en Semana 2',
        status: 'Nuevo (Diagnóstico Completo)',
        leadScore: 'A+',
        prototypeDeadline: 'Semana 2',
        unread: true
      });
      localStorage.setItem('vanguard_client_dossiers', JSON.stringify(existing));
      window.dispatchEvent(new CustomEvent('vanguard:new-dossier', { detail: dossier }));
    } catch (e) {
      console.warn('LocalStorage error syncing dossier:', e);
    }
  }

  handleUserTextInput(text) {
    if (this.state.step === 1) {
      this.state.scope = text;
      this.appendUserMessage(text);
      this.clearChips();
      this.renderStep2('Punto de partida registrado.');
    } else if (this.state.step === 2) {
      this.state.websiteOrSocial = text;
      this.appendUserMessage(text);
      this.clearChips();
      this.renderStep3('Enlaces y presencia digital guardados para auditoría.');
    } else if (this.state.step === 3) {
      this.state.brandStatus = text;
      this.appendUserMessage(text);
      this.clearChips();
      this.renderStep4('Requerimiento de identidad anotado.');
    } else if (this.state.step === 4) {
      this.state.designStyle = text;
      this.appendUserMessage(text);
      this.clearChips();
      this.renderStep5('Dirección visual registrada.');
    } else if (this.state.step === 5) {
      this.state.objective = text;
      this.appendUserMessage(text);
      this.clearChips();
      this.renderStep6();
    } else if (this.state.step === 6) {
      this.appendUserMessage(text);
      this.appendBotMessage('Por favor completa los campos del formulario superior para transferir tu expediente al equipo de diseño y arquitectura.');
    }
  }

  escapeHTML(str) {
    if (!str) return '';
    return str.replace(/[&<>'"]/g, 
      tag => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
    );
  }
}

export function initVanguardAIConsultant() {
  const consultant = new VanguardAIConsultant();
  consultant.init();
}
